import React, { useEffect, forwardRef, useImperativeHandle } from "react";
import { OverviewContainer, OverviewContent } from "./Overview.styled";
import { DetailsAndTTL } from "../../components/DetailsAndTTL";
import StatusAndMetrics from "../../components/StatusAndMetrics";
import { useTriggerForm } from "../../../../hooks/useTriggerForm";
import { useAutosave } from "../../../../../../../hooks/useAutosave";
import { useAutosaveContext } from "../../../../../../../context/AutosaveContext";

const Overview = forwardRef(({ triggerData = null, persistentFormData = null, onFormDataChange, onTriggerSaved }, ref) => {
  const initialData = persistentFormData || triggerData;
  const { showNotification, setError, setSaving } = useAutosaveContext();
  
  const {
    formData,
    errors,
    formState,
    handleNameChange,
    handleDescriptionChange,
    handleTypeChange,
    handleCategoryChange,
    handleTTLValueChange,
    handleTTLUnitChange,
    handleNoTimeLimitChange,
    handleActiveChange,
    getFormData,
    resetForm,
    validateAll,
    isFormValid,
    hasValidationErrors,
  } = useTriggerForm(initialData);

  // Configure autosave
  const {
    registerChange,
    saveImmediately,
    retrySave,
    isSaving,
    lastSaved,
    error,
    hasUnsavedChanges,
  } = useAutosave(
    triggerData,
    (savedData) => {
      showNotification('Changes have been saved automatically', 'success');
      setSaving(false);
    },
    (errorMessage) => {
      setError(errorMessage);
      setSaving(false);
    },
    onTriggerSaved // Callback to notify parent when data is saved
  );

  // Sync saving state with global context
  useEffect(() => {
    setSaving(isSaving);
  }, [isSaving, setSaving]);

  // Expose getFormData, resetForm, validation methods, and autosave methods through ref
  useImperativeHandle(ref, () => ({
    getFormData: () => getFormData(),
    resetForm: () => resetForm(),
    validateAll: () => validateAll(formData),
    isFormValid: () => isFormValid(),
    hasValidationErrors: () => hasValidationErrors(),
    retrySave: () => retrySave(),
    saveImmediately: () => saveImmediately(),
    hasUnsavedChanges: () => hasUnsavedChanges,
    isSaving: () => isSaving,
  }), [getFormData, resetForm, validateAll, formData, isFormValid, hasValidationErrors, retrySave, saveImmediately, hasUnsavedChanges, isSaving]);

  // Generic function to handle onBlur for all fields
  const handleFieldBlurWithAutosave = () => {
    if (hasUnsavedChanges) {
      saveImmediately();
    }
  };

  // Wrapper functions to integrate autosave with form handlers (with onBlur)
  const handleNameChangeWithAutosave = (value) => {
    handleNameChange(value);
    registerChange('name', value);
  };

  const handleDescriptionChangeWithAutosave = (value) => {
    handleDescriptionChange(value);
    registerChange('description', value);
  };

  const handleTypeChangeWithAutosave = (value) => {
    handleTypeChange(value);
    registerChange('workflowType', value);
  };

  const handleCategoryChangeWithAutosave = (value) => {
    handleCategoryChange(value);
    registerChange('category', value);
  };

  const handleTTLValueChangeWithAutosave = (value) => {
    handleTTLValueChange(value);
    registerChange('ttlValue', value);
  };

  const handleTTLUnitChangeWithAutosave = (value) => {
    handleTTLUnitChange(value);
    registerChange('ttlUnit', value);
  };

  const handleNoTimeLimitChangeWithAutosave = (value) => {
    handleNoTimeLimitChange(value);
    registerChange('noTimeLimit', value);
  };

  const handleActiveChangeWithAutosave = (value) => {
    handleActiveChange(value);
    registerChange('active', value);
  };

  useEffect(() => {
    if (onFormDataChange) {
      onFormDataChange(getFormData());
    }
  }, [formData, onFormDataChange, getFormData]);

  return (
    <OverviewContainer>
      <OverviewContent>
        <DetailsAndTTL
          formData={formData}
          errors={errors}
          formState={formState}
          onNameChange={handleNameChangeWithAutosave}
          onNameBlur={handleFieldBlurWithAutosave}
          onDescriptionChange={handleDescriptionChangeWithAutosave}
          onDescriptionBlur={handleFieldBlurWithAutosave}
          onTypeChange={handleTypeChangeWithAutosave}
          onCategoryChange={handleCategoryChangeWithAutosave}
          onTTLValueChange={handleTTLValueChangeWithAutosave}
          onTTLUnitChange={handleTTLUnitChangeWithAutosave}
          onNoTimeLimitChange={handleNoTimeLimitChangeWithAutosave}
        />
        <StatusAndMetrics
          formData={formData}
          onActiveChange={handleActiveChangeWithAutosave}
          triggerData={triggerData}
        />
      </OverviewContent>
    </OverviewContainer>
  );
});

export default Overview;
